// Re-tags live in attrOverrides so the next CDT ETL does not overwrite the item master.
import type { PromotionPlan } from "./cdtLayout/restrictionRemedy";
import type { AppliedPromotion, StudioDraft, StudioItem } from "./studioTypes";
import {
  carvedKeyMapForPromotion,
  dropScopedLevelOrderForPromotion,
  dropScopedMapForPromotion,
  rekeyScopedLevelOrderForPromotion,
  rekeyScopedMapForPromotion,
} from "./studioTypes";

export type { AppliedPromotion };

export const promotionsOf = (draft: StudioDraft): readonly AppliedPromotion[] =>
  draft.promotions ?? [];

export const isPromoted = (draft: StudioDraft, bandVariant: string): boolean =>
  promotionsOf(draft).some((promotion) => promotion.bandVariant === bandVariant);

/** Snapshots exist so revertPromotion can restore orders in any keep-order. */
export const applyPromotion = (
  draft: StudioDraft,
  plan: PromotionPlan,
  items: readonly StudioItem[],
): StudioDraft => {
  if (isPromoted(draft, plan.bandVariant)) return draft;

  const keyMap = carvedKeyMapForPromotion(
    items,
    draft,
    {
      parentValue: plan.parentValue,
      bandVariant: plan.bandVariant,
      skuIds: Object.keys(plan.retag),
    },
    [
      ...Object.keys(draft.scopedValueOrder),
      ...Object.keys(draft.bandOverrides),
      ...Object.keys(draft.orientOverrides),
      ...Object.keys(draft.scopedLevelOrder ?? {}),
    ],
  );

  const attrOverrides: Record<string, Record<string, string | null>> = Object.fromEntries(
    Object.entries(draft.attrOverrides).map(([skuId, attrs]) => [skuId, { ...attrs }]),
  );
  for (const [skuId, value] of Object.entries(plan.retag)) {
    attrOverrides[skuId] = { ...(attrOverrides[skuId] ?? {}), [plan.topAttr]: value };
  }

  const scopedLevelOrder = rekeyScopedLevelOrderForPromotion(
    draft.scopedLevelOrder,
    keyMap,
    plan.parentValue,
    plan.bandVariant,
  );

  return {
    ...draft,
    attrOverrides,
    valueOrder: { ...draft.valueOrder, [plan.topAttr]: plan.valueOrder },
    // The root entry takes the split's order, or the band ranks last.
    scopedValueOrder: {
      ...rekeyScopedMapForPromotion(draft.scopedValueOrder, keyMap),
      "": plan.valueOrder,
    },
    bandOverrides: rekeyScopedMapForPromotion(draft.bandOverrides, keyMap),
    orientOverrides: rekeyScopedMapForPromotion(draft.orientOverrides, keyMap),
    ...(scopedLevelOrder ? { scopedLevelOrder } : {}),
    promotions: [
      ...promotionsOf(draft),
      {
        attr: plan.topAttr,
        parentValue: plan.parentValue,
        bandVariant: plan.bandVariant,
        newValues: plan.newValues,
        skuIds: Object.keys(plan.retag),
        previousValueOrder: draft.valueOrder[plan.topAttr],
        previousRootOrder: draft.scopedValueOrder[""],
      },
    ],
  };
};

/** Put the parent back at the band's index when the carve had consumed it. */
const withoutCarvedValue = (
  order: readonly string[] | undefined,
  promotion: AppliedPromotion,
): readonly string[] | undefined => {
  if (!order) return order;
  const at = order.indexOf(promotion.bandVariant);
  if (at < 0) return order;
  const next = order.filter((value) => value !== promotion.bandVariant);
  if (!next.includes(promotion.parentValue)) next.splice(at, 0, promotion.parentValue);
  return next;
};

const rebasedSnapshots = (
  candidate: AppliedPromotion,
  reverted: AppliedPromotion,
): AppliedPromotion => {
  const previousValueOrder =
    candidate.attr === reverted.attr
      ? withoutCarvedValue(candidate.previousValueOrder, reverted)
      : candidate.previousValueOrder;
  const previousRootOrder = withoutCarvedValue(candidate.previousRootOrder, reverted);
  if (
    previousValueOrder === candidate.previousValueOrder &&
    previousRootOrder === candidate.previousRootOrder
  ) {
    return candidate;
  }
  return { ...candidate, previousValueOrder, previousRootOrder };
};

/** Inherit the reverted snapshots so an absent root stays absent. */
const rootedOnBaseOf = (
  candidate: AppliedPromotion,
  reverted: AppliedPromotion,
): AppliedPromotion => ({
  ...candidate,
  ...(candidate.attr === reverted.attr ? { previousValueOrder: reverted.previousValueOrder } : {}),
  previousRootOrder: reverted.previousRootOrder,
});

/** Snapshots are orders after this carve and later ones are undone — newest restores exactly. */
export const revertPromotion = (draft: StudioDraft, bandVariant: string): StudioDraft => {
  const promotions = promotionsOf(draft);
  const index = promotions.findIndex((candidate) => candidate.bandVariant === bandVariant);
  if (index < 0) return draft;
  const promotion = promotions[index];
  const isNewest = index === promotions.length - 1;

  const attrOverrides: Record<string, Record<string, string | null>> = {};
  for (const [skuId, attrs] of Object.entries(draft.attrOverrides)) {
    if (!promotion.skuIds.includes(skuId)) {
      attrOverrides[skuId] = attrs;
      continue;
    }
    const rest = { ...attrs };
    delete rest[promotion.attr];
    if (Object.keys(rest).length > 0) attrOverrides[skuId] = rest;
  }

  const kept = promotions
    .filter((_, position) => position !== index)
    .map((candidate, position) => {
      if (isNewest || position < index) return candidate;
      return position === 0
        ? rootedOnBaseOf(candidate, promotion)
        : rebasedSnapshots(candidate, promotion);
    });

  const valueOrder = { ...draft.valueOrder };
  const scopedValueOrder: Record<string, readonly string[]> = {
    ...dropScopedMapForPromotion(draft.scopedValueOrder, bandVariant),
  };
  if (isNewest) {
    if (promotion.previousValueOrder) valueOrder[promotion.attr] = promotion.previousValueOrder;
    if (promotion.previousRootOrder) scopedValueOrder[""] = promotion.previousRootOrder;
    else delete scopedValueOrder[""];
  } else {
    const top = withoutCarvedValue(valueOrder[promotion.attr], promotion);
    if (top) valueOrder[promotion.attr] = top;
    const root = withoutCarvedValue(scopedValueOrder[""], promotion);
    if (root) scopedValueOrder[""] = root;
  }

  const scopedLevelOrder = dropScopedLevelOrderForPromotion(draft.scopedLevelOrder, bandVariant);

  const next: StudioDraft = {
    ...draft,
    attrOverrides,
    valueOrder,
    scopedValueOrder,
    bandOverrides: dropScopedMapForPromotion(draft.bandOverrides, bandVariant),
    orientOverrides: dropScopedMapForPromotion(draft.orientOverrides, bandVariant),
    promotions: kept,
  };
  // Deleted, not emptied: a draft back where it started must sign the same.
  if (scopedLevelOrder) next.scopedLevelOrder = scopedLevelOrder;
  else delete next.scopedLevelOrder;
  return next;
};
